/**
 * <lc-header> — Lil'Clucker site header (design: SiteHeader).
 *
 * - Desktop "Shop" / "Shop by need" panels open on hover (with a short intent delay) or click,
 *   close on mouse-leave, Escape, outside click or focus leaving the header.
 * - Search overlay: opens from the search buttons; typing fetches Shopify's predictive search
 *   rendered by sections/lc-search-results.liquid (Section Rendering API); submitting the form
 *   goes to Shopify's search page. Popular-search chips fill the input.
 * - Mobile menu: toggles the drawer under the bar; Shop / Shop by need accordions with one group
 *   open at a time.
 * - Publishes the measured header height on <html> so sticky offsets elsewhere keep working
 *   (`--header-height`, `--header-group-height`, used by Horizon utilities).
 */
class LcHeader extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #typeTimer;
  /** @type {ResizeObserver | null} */
  #resize = null;
  #media = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseover', this.#onMouseOver);
    this.addEventListener('mouseleave', this.#onMouseLeave);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('click', this.#onDocumentClick);
    this.#media.addEventListener('change', this.#onMediaChange);
    this.#observeHeight();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('mouseleave', this.#onMouseLeave);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('click', this.#onDocumentClick);
    this.#media.removeEventListener('change', this.#onMediaChange);
    this.#resize?.disconnect();
  }

  // ------------------------------------------------------------ helpers

  get isDesktop() {
    return this.#media.matches;
  }

  /** @param {string} sel */
  #q(sel) {
    return /** @type {HTMLElement | null} */ (this.querySelector(sel));
  }

  /** Currently open desktop panel key, or null. */
  get openPanel() {
    const open = this.querySelector('[data-lc-panel]:not([hidden])');
    return open instanceof HTMLElement ? open.dataset.lcPanel ?? null : null;
  }

  // ------------------------------------------------------------ desktop menus

  /** @param {string | null} key */
  #setPanel(key) {
    for (const panel of this.querySelectorAll('[data-lc-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.lcPanel !== key;
    }
    for (const button of this.querySelectorAll('[data-lc-menu]')) {
      if (!(button instanceof HTMLElement)) continue;
      const on = button.dataset.lcMenu === key;
      button.setAttribute('aria-expanded', String(on));
      button.classList.toggle('lc-hdr__nav-btn--open', on && button.classList.contains('lc-hdr__nav-btn'));
    }
    this.classList.toggle('lc-hdr--panel-open', Boolean(key));
    if (key) this.#setSearch(false);
  }

  /** @param {Event} event */
  #onMouseOver = (event) => {
    if (!this.isDesktop) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest('.lc-hdr__nav-btn[data-lc-menu]');
    clearTimeout(this.#hoverTimer);
    if (trigger instanceof HTMLElement) {
      const key = trigger.dataset.lcMenu ?? null;
      this.#hoverTimer = setTimeout(() => this.#setPanel(key), this.openPanel ? 0 : 80);
      return;
    }
    if (target.closest('[data-lc-menu-close-on-hover]') && this.openPanel) {
      this.#hoverTimer = setTimeout(() => this.#setPanel(null), 120);
    }
  };

  #onMouseLeave = () => {
    if (!this.isDesktop) return;
    clearTimeout(this.#hoverTimer);
    if (this.openPanel) this.#hoverTimer = setTimeout(() => this.#setPanel(null), 160);
  };

  // ------------------------------------------------------------ search

  /** @param {boolean} open */
  #setSearch(open) {
    const panel = this.#q('[data-lc-search]');
    if (!panel) return;
    panel.hidden = !open;
    this.classList.toggle('lc-hdr--search-open', open);
    for (const button of this.querySelectorAll('[data-lc-search-toggle]')) button.setAttribute('aria-expanded', String(open));
    if (open) {
      this.#setPanel(null);
      this.#setMobileMenu(false);
      const input = this.#q('[data-lc-search-input]');
      if (input instanceof HTMLInputElement) {
        input.focus();
        this.#syncSearchState(input.value);
      }
    } else {
      this.#abort?.abort();
      const input = this.#q('[data-lc-search-input]');
      if (input instanceof HTMLInputElement) input.value = '';
      this.#syncSearchState('');
    }
  }

  /** @param {string} value */
  #syncSearchState(value) {
    const has = value.trim().length > 0;
    const clear = this.#q('[data-lc-search-clear]');
    if (clear) clear.hidden = !has;
    const idle = this.#q('[data-lc-search-idle]');
    const results = this.#q('[data-lc-search-results]');
    const min = Number(this.dataset.searchMin || 2);
    const active = value.trim().length >= min;
    if (idle) idle.hidden = active;
    if (results && !active) results.innerHTML = '';
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-lc-search-input')) return;
    this.#syncSearchState(input.value);
    clearTimeout(this.#typeTimer);
    const term = input.value.trim();
    const min = Number(this.dataset.searchMin || 2);
    if (term.length < min) {
      this.#abort?.abort();
      return;
    }
    this.#typeTimer = setTimeout(() => this.#fetchResults(term), 220);
  };

  /** @param {string} term */
  async #fetchResults(term) {
    const results = this.#q('[data-lc-search-results]');
    if (!results) return;
    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;
    const base = this.dataset.searchUrl || '/search/suggest';
    const section = this.dataset.searchSection || 'lc-search-results';
    const params = new URLSearchParams({
      q: term,
      section_id: section,
      'resources[type]': 'product,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
    });
    results.classList.add('lc-hdr__search-results--loading');
    try {
      const response = await fetch(`${base}?${params.toString()}`, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Predictive search ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector(`#shopify-section-${section}`) ?? doc.body;
      results.innerHTML = inner.innerHTML;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[lc-header] search failed', error);
    } finally {
      results.classList.remove('lc-hdr__search-results--loading');
    }
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-lc-search-form')) return;
    const input = form.querySelector('[data-lc-search-input]');
    if (input instanceof HTMLInputElement && !input.value.trim()) event.preventDefault();
  };

  // ------------------------------------------------------------ mobile menu

  /** @param {boolean} open */
  #setMobileMenu(open) {
    const menu = this.#q('[data-lc-mobile-menu]');
    const toggle = this.#q('[data-lc-menu-toggle]');
    if (!menu || !toggle) return;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    this.classList.toggle('lc-hdr--menu-open', open);
    document.documentElement.classList.toggle('lc-hdr-lock', open && !this.isDesktop);
    if (open) this.#setSearch(false);
    else this.#setMobileTop(null);
  }

  /** @param {string | null} key */
  #setMobileTop(key) {
    for (const panel of this.querySelectorAll('[data-lc-m-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.lcMPanel !== key;
    }
    for (const button of this.querySelectorAll('[data-lc-m-toggle]')) {
      if (!(button instanceof HTMLElement)) continue;
      const on = button.dataset.lcMToggle === key;
      button.setAttribute('aria-expanded', String(on));
      button.classList.toggle('lc-hdr__m-top--open', on);
    }
  }

  /** @param {HTMLElement} button */
  #toggleMobileGroup(button) {
    const panel = button.closest('[data-lc-m-panel]');
    const sub = button.nextElementSibling;
    const open = button.getAttribute('aria-expanded') !== 'true';
    if (panel) {
      for (const other of panel.querySelectorAll('[data-lc-m-group]')) {
        other.setAttribute('aria-expanded', 'false');
        const otherSub = other.nextElementSibling;
        if (otherSub instanceof HTMLElement && otherSub.classList.contains('lc-hdr__m-sub')) otherSub.hidden = true;
      }
    }
    button.setAttribute('aria-expanded', String(open));
    if (sub instanceof HTMLElement && sub.classList.contains('lc-hdr__m-sub')) sub.hidden = !open;
  }

  // ------------------------------------------------------------ events

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const menuButton = target.closest('[data-lc-menu]');
    if (menuButton instanceof HTMLElement && this.contains(menuButton)) {
      const key = menuButton.dataset.lcMenu ?? null;
      clearTimeout(this.#hoverTimer);
      // The strip prompt always opens; nav buttons toggle.
      const toggle = menuButton.classList.contains('lc-hdr__nav-btn');
      this.#setPanel(toggle && this.openPanel === key ? null : key);
      return;
    }

    if (target.closest('[data-lc-search-toggle]')) {
      this.#setSearch(this.#q('[data-lc-search]')?.hidden !== false);
      return;
    }
    if (target.closest('[data-lc-search-close]')) {
      this.#setSearch(false);
      return;
    }
    if (target.closest('[data-lc-search-clear]')) {
      const input = this.#q('[data-lc-search-input]');
      if (input instanceof HTMLInputElement) {
        input.value = '';
        input.focus();
        this.#syncSearchState('');
      }
      this.#abort?.abort();
      return;
    }
    const pick = target.closest('[data-lc-search-pick]');
    if (pick instanceof HTMLElement) {
      const input = this.#q('[data-lc-search-input]');
      if (input instanceof HTMLInputElement) {
        input.value = pick.dataset.lcSearchPick ?? '';
        input.focus();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }
    if (target.closest('[data-lc-search-to-need]')) {
      this.#setSearch(false);
      if (this.isDesktop) this.#setPanel('need');
      else {
        this.#setMobileMenu(true);
        this.#setMobileTop('need');
      }
      return;
    }

    if (target.closest('[data-lc-menu-toggle]')) {
      this.#setMobileMenu(this.#q('[data-lc-mobile-menu]')?.hidden !== false);
      return;
    }
    const top = target.closest('[data-lc-m-toggle]');
    if (top instanceof HTMLElement) {
      const key = top.dataset.lcMToggle ?? null;
      this.#setMobileTop(top.getAttribute('aria-expanded') === 'true' ? null : key);
      return;
    }
    const group = target.closest('[data-lc-m-group]');
    if (group instanceof HTMLElement) {
      this.#toggleMobileGroup(group);
    }
  };

  /** @param {Event} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    if (this.openPanel) this.#setPanel(null);
    if (this.#q('[data-lc-search]')?.hidden === false) this.#setSearch(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#q('[data-lc-search]')?.hidden === false) {
      this.#setSearch(false);
      this.#q('[data-lc-search-toggle]')?.focus();
    } else if (this.openPanel) {
      this.#setPanel(null);
    } else if (this.#q('[data-lc-mobile-menu]')?.hidden === false) {
      this.#setMobileMenu(false);
      this.#q('[data-lc-menu-toggle]')?.focus();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    if (this.isDesktop && this.openPanel) this.#setPanel(null);
  };

  #onMediaChange = () => {
    this.#setPanel(null);
    this.#setMobileMenu(false);
  };

  #observeHeight() {
    const group = document.getElementById('header-group') ?? this;
    const apply = () => {
      const bar = this.#q('.lc-hdr__row');
      const barHeight = bar ? bar.getBoundingClientRect().height : this.getBoundingClientRect().height;
      const groupHeight = group.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${Math.round(barHeight)}px`);
      document.documentElement.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
      document.documentElement.style.setProperty('--lc-header-offset', `${Math.round(this.getBoundingClientRect().top + barHeight)}px`);
    };
    apply();
    if ('ResizeObserver' in window) {
      this.#resize = new ResizeObserver(apply);
      this.#resize.observe(group);
    }
  }
}

if (!customElements.get('lc-header')) customElements.define('lc-header', LcHeader);
